import React, { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const FirebaseImageUpload = ({ onUploadSuccess, folder = 'uploads', allowMultiple = false, maxFiles = 5 }) => {
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (!selectedFiles.length) return;

        let validFiles = selectedFiles.filter(f => f.type.startsWith('image/'));
        if (validFiles.length < selectedFiles.length) {
            setError('Some files were skipped. Please select only image files (PNG, JPG, etc.)');
        } else {
            setError('');
        }

        if (!allowMultiple) {
            validFiles = validFiles.slice(0, 1);
        } else {
            const totalAllowed = maxFiles - files.length;
            if (validFiles.length > totalAllowed) {
                setError(`You can only upload up to ${maxFiles} images.`);
                validFiles = validFiles.slice(0, totalAllowed);
            }
        }

        const newFiles = allowMultiple ? [...files, ...validFiles] : validFiles;
        setFiles(newFiles);
        
        const newPreviews = validFiles.map(f => URL.createObjectURL(f));
        setPreviews(allowMultiple ? [...previews, ...newPreviews] : newPreviews);
    };

    const removeFile = (index) => {
        const updatedFiles = [...files];
        updatedFiles.splice(index, 1);
        setFiles(updatedFiles);

        const updatedPreviews = [...previews];
        updatedPreviews.splice(index, 1);
        setPreviews(updatedPreviews);
    };

    const handleUpload = async () => {
        if (!files.length) return;

        setIsUploading(true);
        setError('');
        setProgress(0);
        
        const downloadURLs = [];
        let completedCount = 0;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const filename = `${Date.now()}-${file.name}`;
            const storageRef = ref(storage, `${folder}/${filename}`);
            
            try {
                const uploadTask = uploadBytesResumable(storageRef, file);
                
                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            const fileProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            const overallProgress = Math.round(((completedCount * 100) + fileProgress) / files.length);
                            setProgress(overallProgress);
                        },
                        (err) => reject(err),
                        async () => {
                            try {
                                const url = await getDownloadURL(uploadTask.snapshot.ref);
                                downloadURLs.push(url);
                                completedCount++;
                                resolve();
                            } catch (err) {
                                reject(err);
                            }
                        }
                    );
                });
            } catch (err) {
                console.error("Upload error:", err);
                setError('Failed to upload image(s). Please try again.');
                setIsUploading(false);
                return;
            }
        }

        setIsUploading(false);
        if (allowMultiple) {
            onUploadSuccess(downloadURLs);
        } else {
            onUploadSuccess(downloadURLs[0]);
        }
    };

    const canAddMore = allowMultiple && files.length < maxFiles;

    return (
        <div className="w-full">
            {(!files.length || canAddMore) && !isUploading && (
                <div className="flex flex-col sm:flex-row gap-4 w-full mb-4">
                    <label className="cursor-pointer bg-sky-50 text-sky-700 border-2 border-sky-200 hover:border-sky-400 hover:bg-sky-100 rounded-xl px-4 py-4 font-bold flex flex-col items-center flex-1 transition-all shadow-sm text-center">
                        <i className="fas fa-camera text-2xl mb-2"></i>
                        <span>Take Photo</span>
                        <input 
                            type="file" 
                            accept="image/*"
                            capture="environment"
                            multiple={allowMultiple}
                            onChange={handleFileChange} 
                            disabled={isUploading}
                            className="hidden"
                        />
                    </label>
                    <label className="cursor-pointer bg-slate-50 text-slate-600 border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-100 rounded-xl px-4 py-4 font-bold flex flex-col items-center flex-1 transition-all shadow-sm text-center">
                        <i className="fas fa-images text-2xl mb-2"></i>
                        <span>Upload File</span>
                        <input 
                            type="file" 
                            accept="image/*" 
                            multiple={allowMultiple}
                            onChange={handleFileChange} 
                            disabled={isUploading}
                            className="hidden"
                        />
                    </label>
                </div>
            )}
            
            {previews.length > 0 && (
                <div className="flex flex-wrap gap-4 justify-center mb-4">
                    {previews.map((preview, index) => (
                        <div key={index} className="relative">
                            <img src={preview} alt="Preview" className="w-24 h-24 object-cover rounded-xl border-2 border-sky-200 shadow-sm" />
                            {!isUploading && progress === 0 && (
                                <button 
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors text-xs"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            
            {files.length > 0 && !isUploading && progress === 0 && (
                <button 
                    onClick={handleUpload}
                    type="button"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-colors flex justify-center items-center gap-2"
                >
                    <i className="fas fa-cloud-upload-alt"></i>
                    Upload {files.length > 1 ? `${files.length} Images` : 'Image'}
                </button>
            )}

            {isUploading && (
                <div className="w-full mt-4">
                    <div className="flex justify-between text-sm font-bold text-sky-700 mb-1">
                        <span>Uploading...</span>
                        <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                        <div className="bg-sky-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>
            )}

            {error && <p className="text-red-500 text-sm font-bold text-center mt-3">{error}</p>}
        </div>
    );
};

export default FirebaseImageUpload;
